/** Pure, DOM-free primitives for pull-to-refresh, kept beside `dragGesture.ts` for
 * the same reason: the thresholds and the rubber-band curve are the part worth unit testing, and
 * they must have one implementation rather than one per screen that pulls. */

/** Pulled distance, in px, at which a release refreshes. Below it the pull snaps back and fires
 * nothing. */
export const PULL_TRIGGER_DISTANCE = 60

/** Asymptote of the travel curve. The content approaches it and never reaches it — there is no
 * hard stop for the finger to hit. */
export const PULL_MAX_DISTANCE = 120

/** Once armed, the pull only disarms a few px back down the travel, so a finger resting on the
 * threshold can't stutter the arm haptic on and off. */
export const PULL_DISARM_DISTANCE = PULL_TRIGGER_DISTANCE - 4

/** Whether a touch beginning at this document scroll position may start a pull. Only a page
 * already at the top: anywhere else the same downward drag is an ordinary scroll. */
export function canArmPull(scrollY: number): boolean {
  return scrollY <= 0
}

/** Elastic travel for a downward drag of `dy` px. Tracks the finger about 1:1 for
 * the first few pixels, then resists progressively, so the page reads as stretching rather than
 * as having come unstuck or as having jammed. Trigger lands at ~83px of finger travel. */
export function resolvePullDistance(dy: number): number {
  if (dy <= 0) return 0
  return PULL_MAX_DISTANCE * (1 - Math.exp(-dy / PULL_MAX_DISTANCE))
}

/** Whether a released pull refreshes. */
export function resolvePullCommit(distance: number): boolean {
  return distance >= PULL_TRIGGER_DISTANCE
}

/** Armed state at this distance, given what it was a frame ago. Hysteresis is deliberate: see
 * `PULL_DISARM_DISTANCE`. */
export function resolvePullArmed(distance: number, wasArmed: boolean): boolean {
  return wasArmed ? distance >= PULL_DISARM_DISTANCE : resolvePullCommit(distance)
}
